export function shuffle<A>(array: A[]): A[] {
  return array.map((_, i, arrCopy) => {
    const rand = i + Math.floor(Math.random() * (arrCopy.length - i))

    // eslint-disable-next-line no-param-reassign
    ;[arrCopy[rand], arrCopy[i]] = [arrCopy[i], arrCopy[rand]]

    return arrCopy[i]
  })
}

export function join<A>(xs: A[][]): A[] {
  return xs.reduce((left, right) => left.concat(right), [])
}

export function flatMap<A, B>(xs: A[], f: (x: A) => B[]): B[] {
  return join(xs.map(f))
}

export function uniqueBy<A>(xs: A[], by: (x: A) => string): A[] {
  const seen: Record<string, boolean> = {}

  return xs.filter((item) => {
    if (seen[by(item)]) {
      return false
    }

    seen[by(item)] = true

    return true
  })
}

export function unique(xs: string[]): string[] {
  return uniqueBy(xs, (x) => x)
}

type GroupObject<A> = {
  key: string | number
  values: A[]
}

export const groupBy = <A>(xs: A[], getKey: (x: A) => string | number): A[][] => {
  // Note: the previous implementation of this function was faster, the `.find` inside the reduce loop gives the
  // current implementation a time complexity of O(n^2).
  const grouped: GroupObject<A>[] = xs.reduce((results: GroupObject<A>[], value: A) => {
    const key = getKey(value)
    const group = results.find((val) => val?.key === key)

    if (!group) {
      results.push({
        key,
        values: [value],
      })
    } else {
      group.values.push(value)
    }

    return results
  }, [] as GroupObject<A>[])

  return Object.values(grouped).map((val) => val.values)
}

export function sortBy<A>(xs: A[], fn: (x: A) => number | string, reverse = false): A[] {
  return [...xs].sort((left, right) => {
    const leftResult = fn(left)
    const rightResult = fn(right)

    if (leftResult < rightResult) {
      return reverse ? 1 : -1
    }

    if (leftResult > rightResult) {
      return reverse ? -1 : 1
    }

    return 0
  })
}

export function intersectionBy<A, B>(fn: (x: A) => B) {
  return (left: A[], right: A[]): A[] => left.filter((x) => right.map(fn).includes(fn(x)))
}

export function init<A>(xs: A[]): A[] {
  return [...xs].slice(0, -1)
}

export function last<A>(xs: A[]): A {
  return xs[xs.length - 1]
}

export function tail<A>(xs: A[]): A[] {
  return [...xs].slice(1)
}

export function takeWhile<A>(xs: A[], fn: (x: A) => boolean): A[] {
  const index = xs.findIndex((x) => !fn(x))
  return xs.slice(0, index < 0 ? xs.length : index)
}

export function dropWhile<A>(xs: A[], fn: (x: A) => boolean): A[] {
  const index = xs.findIndex((x) => !fn(x))
  return xs.slice(index < 0 ? xs.length : index, xs.length)
}

export const sum = (xs: number[]): number => xs.reduce((x, acc) => x + acc, 0)

export function intersperse<A>(arr: A[], sep: A, finalSep: A = sep): A[] {
  if (arr.length === 0) {
    return []
  }

  return arr.slice(1).reduce((xs, x, index) => xs.concat([index === arr.length - 2 ? finalSep : sep, x]), [arr[0]])
}
